'use client'

// Public contact form → POST /api/contact (go-live audit: was mailto-only).
// Kept intentionally dependency-light: native inputs + the shared Button.

import { useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/button'

type Status = 'idle' | 'sending' | 'sent' | 'error'

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (status === 'sending') return
    setStatus('sending')
    setError(null)

    const form = e.currentTarget
    const data = new FormData(form)
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: String(data.get('name') ?? '').trim() || undefined,
          email: String(data.get('email') ?? '').trim(),
          subject: String(data.get('subject') ?? '').trim() || undefined,
          message: String(data.get('message') ?? '').trim(),
        }),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null
        throw new Error(body?.error ?? 'Something went wrong. Please email us directly.')
      }
      form.reset()
      setStatus('sent')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.')
      setStatus('error')
    }
  }

  if (status === 'sent') {
    return (
      <div
        role="status"
        className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-4 text-sm"
      >
        <p className="font-medium text-foreground">Message sent — thank you!</p>
        <p className="mt-1 text-muted-foreground">
          We read every message and usually reply within one business day.
        </p>
      </div>
    )
  }

  const inputClass =
    'w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand/40'

  return (
    <form onSubmit={onSubmit} className="space-y-4" aria-label="Contact form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-1 block text-sm font-medium">
            Name <span className="text-muted-foreground">(optional)</span>
          </label>
          <input id="contact-name" name="name" maxLength={120} className={inputClass} />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-1 block text-sm font-medium">
            Email
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            maxLength={254}
            className={inputClass}
            placeholder="you@example.com"
          />
        </div>
      </div>
      <div>
        <label htmlFor="contact-subject" className="mb-1 block text-sm font-medium">
          Subject <span className="text-muted-foreground">(optional)</span>
        </label>
        <input id="contact-subject" name="subject" maxLength={200} className={inputClass} />
      </div>
      <div>
        <label htmlFor="contact-message" className="mb-1 block text-sm font-medium">
          Message
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          minLength={10}
          maxLength={5000}
          rows={6}
          className={inputClass}
          placeholder="How can we help?"
        />
      </div>
      {error && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
      <Button type="submit" disabled={status === 'sending'}>
        {status === 'sending' ? 'Sending…' : 'Send message'}
      </Button>
    </form>
  )
}
